import { useState, useEffect, useRef } from "react";
// material
import { Card, Container } from "@mui/material";
// components
import Scrollbar from "../table/Scrollbar";
import TableView from "../Table";
import {
  parseCurrency,
  parseDateTime,
  formatDateFilter,
  isEmptyFilter,
} from "../../utils/tableFunctions";
import {
  getTransactionFilterParam,
  getTransactionList,
  getTransactionCSV,
} from "../../services/reconciliations";
import moment from "moment";

import { rowsPerPage } from "../../utils/constants";
import HeaderHelpPage from "../HeaderHelpPage";
import { Log } from "../../services/services";
import FormSearch from "../FormSearch";
import CircularLoader from "../circularLoader";
import SearchPrompt from "../SearchPrompt";
import CommonDialog from "../CustomDialog";
import TransactionDetails from "../../pages/Reconciliations/TransactionDetails";
import { TRANSACTION_STATE_LIST, TRANSACTION_TYPE_LIST } from "../../utils/constants";
// ----------------------------------------------------------------------

const align = {
  "base_amount": "right",
  "transaction_amount": "right",
  "transaction_currency": "center",
  "base_currency": "center",
  "method": "center",
  "type": "center",
  "amount": "right"
};

const style = {
  "amount": {
    whiteSpace: "nowrap"
  }
}

const defaultValue = {
  start_datetime: moment().subtract(1, 'days'),
  end_datetime: moment()
}

let filter = {
  start_datetime: moment().subtract(1, 'days'),
  end_datetime: moment()
}

export default function TransactionList({ userRole }) {
  const [transaction, setTransaction] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState();
  const [filterName, setFilterName] = useState();
  const [fParams, setFParams] = useState({});
  const [fLoaded, setFLoaded] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const useRefBusinessEntityList = useRef([]);
  const [itemId, setItemId] = useState(null);
  // the list is only queried once the user has searched, so the page opens fast
  const [searched, setSearched] = useState(false);

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: rowsPerPage,
  });

  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the page used to
    // load with on first render
    const filterParams = isEmptyFilter(params) ? { ...defaultValue } : params;
    const { start_datetime, end_datetime, merchant_name_list, transaction_id, terminal_id, reference_number, host_trace_no } = filterParams;
    filter = {
      ...filterParams,
      transaction_id: transaction_id ? transaction_id.trim() : transaction_id,
      terminal_id: terminal_id ? terminal_id.trim() : terminal_id,
      reference_number: reference_number ? reference_number.trim() : reference_number,
      host_trace_no: host_trace_no ? host_trace_no.trim() : host_trace_no,
      start_datetime:
        start_datetime && moment(start_datetime).isValid()
          ? formatDateFilter(start_datetime, "start")
          : null,
      end_datetime:
        end_datetime && moment(end_datetime).isValid()
          ? formatDateFilter(end_datetime, "end")
          : null,
      merchant_name_list: merchant_name_list?.length ? merchant_name_list : fParams?.merchant_by_business_entity?.length ? fParams?.merchant_by_business_entity : null,
      merchant_name_input: inputValues?.merchant_name_list || null
    };

    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Click button [Apply Filter] - Search Transaction list", filter);
    setSearched(true);
    setLoaded(false);
    getTransactionList((data) => {
      setTransaction(data);
      setPagination(newPagination);
    }, setLoaded, false, filter, newPagination);
  };

  // ------------------ end of filter stuffies --------------------//

  const getCSVData = async () => {
    const { start_datetime, end_datetime } = filter;
    const newFilter = {
      ...filter,
      start_datetime:
        start_datetime && moment(start_datetime).isValid()
          ? formatDateFilter(start_datetime, "start")
          : null,
      end_datetime:
        end_datetime && moment(end_datetime).isValid()
          ? formatDateFilter(end_datetime, "end")
          : null,
    };
    const data = await getTransactionCSV(newFilter);
    return data;
  };

  const getNext = async (params) => {
    const newPagination = {
      ...pagination,
      ...params,
    };

    const { start_datetime, end_datetime } = filter;
    const newFilter = {
      ...filter,
      start_datetime:
        start_datetime && moment(start_datetime).isValid()
          ? formatDateFilter(start_datetime, "start")
          : null,
      end_datetime:
        end_datetime && moment(end_datetime).isValid()
          ? formatDateFilter(end_datetime, "end")
          : null,
    };

    setLoaded(false);
    getTransactionList((data) => {
      setTransaction(data);
      setPagination(newPagination);
    }, setLoaded, false, newFilter, newPagination);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const merchant_name = queryParams.get("merchant_name");
    const newFilter = {
      ...filter,
      start_datetime: formatDateFilter(filter.start_datetime, "start"),
      end_datetime: formatDateFilter(filter.end_datetime, "end"),
    };

    getTransactionFilterParam(setFParams, setFLoaded);

    // only auto-query when the page is opened through a deep link that already
    // carries a filter, otherwise wait for the user to search
    if (merchant_name) {
      newFilter["merchant_name_list"] = [merchant_name];
      newFilter["start_datetime"] = null;
      newFilter["end_datetime"] = null;
      filter = newFilter;
      setSearched(true);
      getTransactionList(setTransaction, setLoaded, false, newFilter, pagination);
    }
  }, []);

  const formatData = (input) => {
    let tableData = [];
    input?.results?.forEach(
      ({
        id,
        original_id,
        base_amount,
        base_currency,
        business_entity,
        merchant,
        payment_brand,
        terminal,
        transaction_amount,
        transaction_currency,
        transaction_datetime,
        transaction_state,
        transaction_type,
        payment_host,
        payment_method,
        reference_number,
        host_trace_no,
      }) => {
        tableData.push({
          id: id,
          transaction_ID: id,
          terminal_ID: terminal && terminal.terminal_id,
          // original_id: original_id && original_id,
          business_entity: business_entity && business_entity.business_name,
          merchant: merchant && merchant.name,
          transaction_time:
            transaction_datetime && parseDateTime(transaction_datetime),
          // method: payment_method && payment_method.name,
          // brand: payment_brand && payment_brand.name,
          host: payment_host && payment_host.name,
          // base_currency: base_currency && base_currency,
          // base_amount: base_amount && parseCurrency(base_amount),
          // currency: transaction_currency && transaction_currency,
          amount: transaction_currency && transaction_amount ? 
            `${parseCurrency(transaction_amount, transaction_currency)} ${transaction_currency}` : "",
          type: transaction_type && transaction_type.transaction_type,
          state: transaction_state && transaction_state.transaction_state,
          reference_number: reference_number,
          host_trace_no: host_trace_no,
        });
      }
    );
    return tableData;
  };

  useEffect(() => {
    if (transaction) {
      setTableData(formatData(transaction));
    }
  }, [transaction]);

  const schema = [
    {
      type: "text",
      label: "Transaction ID",
      param: "transaction_id",
    },
    {
      type: "text",
      label: "Terminal ID",
      param: "terminal_id",
    },
    {
      type: "multi-select",
      label: "Business Entity",
      param: "business_entity_list",
      options: fParams?.business_entity?.map((be) => ({
        value: be ? be.id : "",
        label: be ? be.business_name : "",
      })),
      inputValues,
      setInputValues,
      width: 6
    },
    {
      type: "multi-select",
      label: "Merchant Name",
      param: "merchant_name_list",
      options: (fParams?.merchant_by_business_entity?.length > 0 || inputValues?.business_entity_list) ? fParams?.merchant_by_business_entity?.map((name) => ({
        value: name ? name : "",
        label: name ? name : "",
      })) : fParams?.merchant_name?.map((name) => ({
        value: name ? name : "",
        label: name ? name : "",
      })),
      inputValues,
      setInputValues,
      width: 6
    },
    {
      type: "daterange",
      label: "Transaction Time",
      param: ["start_datetime", "end_datetime"],
      width: 6
    },
    {
      type: "multi-select",
      label: "Payment Host",
      param: "payment_host_list",
      options: fParams?.payment_host?.map((name) => ({
        value: name ? name : "",
        label: name ? name : "",
      })),
    },
    {
      type: "multi-select",
      label: "Currency",
      param: "transaction_currency_list",
      options: fParams?.base_currency?.map((name) => ({
        value: name ? name : "",
        label: name ? name : "",
      })),
    },
    {
      type: "number",
      label: "Min Amount",
      param: "min_transaction_amount",
      width: 1.5
    },
    {
      type: "number",
      label: "Max Amount",
      param: "max_transaction_amount",
      width: 1.5
    },
    {
      type: "text",
      label: "Ref Number",
      param: "reference_number",
      width: 1.5
    },
    {
      type: "text",
      label: "Host Trace No.",
      param: "host_trace_no",
      width: 1.5
    },
    {
      type: "multi-select",
      label: "Type",
      param: "transaction_type_list",
      options: TRANSACTION_TYPE_LIST?.map((name) => ({
        value: name ? name?.toLowerCase() : "",
        label: name ? name : "",
      })),
    },
    {
      type: "multi-select",
      label: "State",
      param: "transaction_state_list",
      options: TRANSACTION_STATE_LIST?.map((name) => ({
        value: name ? name?.toLowerCase() : "",
        label: name ? name : "",
      })),
    },
  ]

  const updateMerchantByBusinessEntity = (searchText) => {
    if (searchText) {
      setFParams((prev) => ({
        ...prev,
        merchant_by_business_entity: [
          ...(useRefBusinessEntityList.current || []),
          ...(prev.business_entity
            ?.filter((be) =>
              be.business_name
                ?.toLowerCase()
                ?.includes(searchText.toLowerCase())
            )
            ?.flatMap((be) => be.merchant_names) || []),
        ],
      }));
    } else {
      setFParams((prev) => ({
        ...prev,
        merchant_by_business_entity: useRefBusinessEntityList.current,
      }));
    }
  };

  useEffect(() => {
    updateMerchantByBusinessEntity(inputValues?.business_entity_list);
  }, [inputValues?.business_entity_list]);

  const onChangeBusinessEntity = (value) => {
    if (value.business_entity_list) {
      const filteredMerchantNames = fParams.business_entity
        ?.filter(be => value.business_entity_list?.includes(be.id))
        ?.flatMap(be => be.merchant_names) || [];

      useRefBusinessEntityList.current = filteredMerchantNames;
      updateMerchantByBusinessEntity(inputValues?.business_entity_list);
    } else {
      useRefBusinessEntityList.current = [];
      updateMerchantByBusinessEntity('');
    }
  };

  return (
    <Container>
      <HeaderHelpPage title="Transactions" description="This page manages all LIVE transactions" />
      
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        onChangeBusinessEntity={onChangeBusinessEntity}
        getCSVData={transaction.count > 0 ? getCSVData : false}
        type="reconciled_transactions"
        csv={true}
        defaultValue={defaultValue}
      />

      {!searched ? (
        <SearchPrompt />
      ) : loaded ? (
        <Card>
          <Scrollbar>
            <TableView
              rows={tableData}
              link={`/3/app/details/transaction`}
              // onSelectItem={(id) => setItemId(id)}
              getNext={getNext}
              count={transaction.count}
              query={filterName}
              pagination={pagination}
              data={transaction?.results}
              align={align}
              style={style}
            />
          </Scrollbar>
        </Card>
      ) : (
        <CircularLoader />
      )}

      {itemId && (
        <CommonDialog
          title="Transaction Details"
          onClose={() => setItemId(null)}
        >
          <TransactionDetails userRole={userRole} itemId={itemId} />
        </CommonDialog>
      )}
    </Container>
  );
}
